import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import CoinIcon from '../components/CoinIcon';
import TradeDetailPanel from '../components/TradeDetailPanel';
import { HL_WALLET, HL_URL } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './History.css';

function formatDateTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatPrice(price) {
  if (price == null || price === 0) return '—';
  if (price >= 10000) return `$${Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1000) return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${Number(price).toFixed(4)}`;
  return `$${Number(price).toPrecision(4)}`;
}

function toDateInput(date) {
  return date.toISOString().slice(0, 10);
}

function tsMs(s) {
  return s.timestamp?.toDate ? s.timestamp.toDate().getTime()
       : s.timestamp ? new Date(s.timestamp).getTime() : 0;
}

const SORT_KEYS = {
  id:       s => parseInt((s.tradeId || '').replace('#', ''), 10) || 0,
  side:     s => s.direction || '',
  symbol:   s => s.symbol || '',
  strategy: s => s.strategy || s.source || '',
  entry:    s => s.fill_price || s.price || 0,
  sl:       s => s.sl_price || 0,
  tp:       s => s.tp_price || 0,
  size:     s => s.size || 0,
  pnl:      s => s._pnl ?? -Infinity,
  time:     s => tsMs(s),
};

function csvEscape(v) {
  return `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
}

function History() {
  const [signals, setSignals]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allFills, setAllFills]       = useState([]);

  const [symbolFilter,   setSymbolFilter]   = useState('ALL');
  const [sideFilter,     setSideFilter]     = useState('ALL');
  const [strategyFilter, setStrategyFilter] = useState('ALL');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');

  const [sortCol, setSortCol] = useState('time');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const q = query(collection(db, 'signals'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setSignals(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => s.status !== 'duplicate_skipped' && s.action !== 'CLOSE' && s.tradeId)
      );
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Fetch all HL fills once — used to compute per-row PnL without N API calls
  useEffect(() => {
    fetch(HL_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ type: 'userFills', user: HL_WALLET }),
    })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllFills(data); })
      .catch(() => {});
  }, []);

  const symbols = useMemo(() => {
    const set = new Set(signals.map(s => s.symbol).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [signals]);

  const strategies = useMemo(() => {
    const set = new Set(signals.map(s => s.strategy || s.source).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [signals]);

  // Pre-compute nextSignalMs for every signal — avoids O(n²) in render
  const nextMsMap = useMemo(() => {
    const map = {};
    signals.forEach(sig => {
      const sigMs = tsMs(sig);
      const later = signals.filter(s => s.symbol === sig.symbol && s.id !== sig.id && tsMs(s) > sigMs && s.status !== 'duplicate_skipped');
      // signals is ordered newest-first, so the *last* entry in `later` is the
      // nearest one after sig (i.e. the true "next" trade) — matches Dashboard.js
      map[sig.id] = later.length > 0 ? tsMs(later[later.length - 1]) : null;
    });
    return map;
  }, [signals]);

  // Most recent executed signal per symbol — only "open" if no HL closing fills found after it
  const openSignalIds = useMemo(() => {
    const latestBySymbol = {};
    signals.forEach(s => {
      if (s.status !== 'executed') return;
      const ms = tsMs(s);
      if (!latestBySymbol[s.symbol] || ms > latestBySymbol[s.symbol].ms) {
        latestBySymbol[s.symbol] = { id: s.id, ms };
      }
    });
    const result = new Set();
    Object.values(latestBySymbol).forEach(({ id, ms }) => {
      const sig = signals.find(s => s.id === id);
      if (!sig) return;
      const coin     = (sig.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const dirLabel = (sig.direction || '').toUpperCase() === 'LONG' ? 'Long' : 'Short';
      const hasClosed = allFills.some(f =>
        f.coin === coin &&
        (f.dir || '').includes(dirLabel) &&
        (f.dir || '').includes('Close') &&
        f.time >= ms
      );
      if (!hasClosed) result.add(id);
    });
    return result;
  }, [signals, allFills]);

  function computePnl(signal) {
    const result = tradeResult(signal, allFills, nextMsMap[signal.id]);
    return result ? result.net : null;
  }

  const sorted = useMemo(() => {
    const filtered = signals.filter(s => {
      if (symbolFilter !== 'ALL' && s.symbol !== symbolFilter) return false;
      if (sideFilter !== 'ALL' && (s.direction || '').toUpperCase() !== sideFilter) return false;
      if (strategyFilter !== 'ALL' && (s.strategy || s.source) !== strategyFilter) return false;
      if (dateFrom || dateTo) {
        const ts = s.timestamp?.toDate ? s.timestamp.toDate() : s.timestamp ? new Date(s.timestamp) : null;
        if (!ts) return false;
        if (dateFrom && ts < new Date(dateFrom)) return false;
        if (dateTo && ts > new Date(dateTo + 'T23:59:59')) return false;
      }
      return true;
    });

    // Attach _pnl for sort key access
    const withPnl = filtered.map(s => ({ ...s, _pnl: computePnl(s) }));

    const keyFn = SORT_KEYS[sortCol] || SORT_KEYS.time;
    return [...withPnl].sort((a, b) => {
      const av = keyFn(a), bv = keyFn(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, allFills, symbolFilter, sideFilter, strategyFilter, dateFrom, dateTo, sortCol, sortDir]);

  function clearFilters() {
    setSymbolFilter('ALL');
    setSideFilter('ALL');
    setStrategyFilter('ALL');
    setDateFrom('');
    setDateTo('');
  }

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleSort(col) {
    setSortCol(prev => {
      if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col; }
      setSortDir('asc');
      return col;
    });
  }

  function downloadCSV() {
    const headers = ['ID', 'Side', 'Symbol', 'Strategy', 'Entry', 'Exit', 'SL', 'TP', 'Size', 'Est PnL', 'Duration', 'Exit Reason', 'Time'];
    const rows = sorted.map(s => {
      const coin = (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const ts   = s.timestamp?.toDate ? s.timestamp.toDate().toISOString()
                 : s.timestamp ? new Date(s.timestamp).toISOString() : '';
      const isOpen = openSignalIds.has(s.id);
      const pnl  = isOpen ? 'OPEN' : s._pnl != null ? `${s._pnl > 0 ? '+' : ''}${s._pnl.toFixed(4)}` : '';
      const exitReason = isOpen ? 'Position open' : (s.exit_reason || '');
      return [
        s.tradeId || '',
        s.direction || '',
        coin,
        s.strategy || s.source || '',
        s.fill_price || s.price || '',
        '',
        s.sl_price || '',
        s.tp_price || '',
        s.size != null ? `$${s.size}` : '',
        pnl,
        '',
        exitReason,
        ts,
      ];
    });
    const csv  = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `spinningcat-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function SortTh({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={`sortable-th${active ? ' sort-active' : ''}`} onClick={() => handleSort(col)}>
        {children}
        <span className="sort-arrow">{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </th>
    );
  }

  const hasActiveFilter = symbolFilter !== 'ALL' || sideFilter !== 'ALL' || strategyFilter !== 'ALL' || dateFrom || dateTo;

  return (
    <div>
      <div className="page-header">
        <h1>Signal History</h1>
        <p>All past signals from TradingView alerts</p>
      </div>

      <div className="history-toolbar card">
        <div className="filter-group">
          <label className="filter-label">Side</label>
          <div className="side-filter-btns">
            {['ALL', 'LONG', 'SHORT'].map(side => (
              <button
                key={side}
                className={`side-btn side-btn--${side.toLowerCase()}${sideFilter === side ? ' active' : ''}`}
                onClick={() => setSideFilter(side)}
              >
                {side}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Symbol</label>
          <select className="filter-select" value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)}>
            {symbols.map(s => (
              <option key={s} value={s}>{s === 'ALL' ? 'All Symbols' : s.replace(/USDT$/, '')}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Strategy</label>
          <select className="filter-select" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
            {strategies.map(s => (
              <option key={s} value={s}>{s === 'ALL' ? 'All Strategies' : s}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">From</label>
          <input type="date" className="filter-select" value={dateFrom}
            max={dateTo || toDateInput(new Date())} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="filter-group">
          <label className="filter-label">To</label>
          <input type="date" className="filter-select" value={dateTo}
            min={dateFrom} max={toDateInput(new Date())} onChange={e => setDateTo(e.target.value)} />
        </div>

        <div className="history-toolbar-right">
          {hasActiveFilter && <button className="clear-btn" onClick={clearFilters}>Clear</button>}
          <button className="csv-btn" onClick={downloadCSV} disabled={sorted.length === 0}>↓ CSV</button>
          <span className="history-count">{sorted.length} trade{sorted.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, overflowX: 'auto' }}>
        {loading && (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading history...</p>
          </div>
        )}

        {!loading && sorted.length === 0 && (
          <div className="empty-state">
            <span className="icon">◷</span>
            <p>{hasActiveFilter ? 'No trades match your filters.' : 'No trade history yet.'}</p>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <table className="data-table history-table">
            <thead>
              <tr>
                <SortTh col="id">ID</SortTh>
                <SortTh col="side">Side</SortTh>
                <SortTh col="symbol">Symbol</SortTh>
                <SortTh col="strategy">Strategy</SortTh>
                <SortTh col="entry">Entry</SortTh>
                <SortTh col="sl">SL</SortTh>
                <SortTh col="tp">TP</SortTh>
                <SortTh col="size">Size</SortTh>
                <SortTh col="pnl">PnL</SortTh>
                <SortTh col="time">Time</SortTh>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(signal => {
                const coin      = (signal.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
                const isLong    = (signal.direction || '').toUpperCase() !== 'SHORT';
                const open      = expandedIds.has(signal.id);
                const isOpenPos = openSignalIds.has(signal.id);
                const pnl       = signal._pnl;
                const nextSignalMs = nextMsMap[signal.id] ?? null;
                return [
                  <tr
                    key={signal.id}
                    className={[
                      open ? 'row--expanded' : '',
                      isOpenPos ? 'row--open-position' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(signal.id)}
                  >
                    <td className="hist-trade-id">{signal.tradeId || '—'}</td>
                    <td>
                      <span className={`side-badge side-badge--${isLong ? 'long' : 'short'}`}>
                        {isLong ? 'LONG' : 'SHORT'}
                      </span>
                      {' '}
                      <span className="leverage-badge">{signal.leverage || 5}x {signal.leverageType || 'Cross'}</span>
                    </td>
                    <td>
                      <div className="history-coin">
                        <CoinIcon base={coin} size={18} />
                        <span className="history-symbol">{coin}</span>
                      </div>
                    </td>
                    <td className="strategy-cell">{signal.strategy || signal.source || '—'}</td>
                    <td>{formatPrice(signal.fill_price || signal.price)}</td>
                    <td className="text-red">{formatPrice(signal.sl_price)}</td>
                    <td className="text-green">{formatPrice(signal.tp_price)}</td>
                    <td>{signal.size != null ? `$${signal.size}` : '—'}</td>
                    <td className={isOpenPos ? 'pnl-open' : pnl != null ? (pnl >= 0 ? 'pnl-positive' : 'pnl-negative') : 'pnl-open'}>
                      {isOpenPos
                        ? 'OPEN'
                        : pnl != null ? `${pnl >= 0 ? '+' : '−'}$${Math.abs(pnl).toFixed(2)}` : '—'}
                    </td>
                    <td className="time-cell">{formatDateTime(signal.timestamp)}</td>
                    <td className="td-expand-col">{open ? '▲' : '▼'}</td>
                  </tr>,
                  open && (
                    <tr key={`${signal.id}-detail`} className="row--detail">
                      <td colSpan="11">
                        <TradeDetailPanel signal={signal} nextSignalMs={nextSignalMs} />
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default History;
